/**
 * Service Act editor (2026-06-03).
 * Спрощена версія GoodsSaleEditor — без складу, без серій.
 * Тільки номенклатура з is_service=true.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceActs, type ServiceActItem } from '@/api/serviceActs';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';
import ProjectSelect from '@/components/ProjectSelect';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';


interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  project_id: string | null;
  service_period_from: string;
  service_period_to: string;
  prices_include_vat: boolean;
  comment: string;
  items: ServiceActItem[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  project_id: null,
  service_period_from: '',
  service_period_to: '',
  prices_include_vat: false,
  comment: '',
  items: [],
};

const newRow = (
  n: number, vatId: string, taxPurposeId: string | null,
): ServiceActItem => ({
  line_number: n,
  nomenclature_id: '',
  description: '',
  quantity: '1',
  price: '0',
  amount_without_vat: '0',
  vat_rate_id: vatId,
  vat_amount: '0',
  total_amount: '0',
  tax_purpose_id: taxPurposeId,
  accounting_account_code: '703',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function ServiceActEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('sales.service_act.manage');

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const buyers = useMemo(
    () => contractorsQ.data?.items.filter((c) => c.is_buyer) ?? [],
    [contractorsQ.data],
  );

  // ⚠ Тільки is_service=true.
  const nomListQ = useQuery({
    queryKey: ['nomenclature-services'],
    queryFn: () => nomApi.list({ limit: 1000 }),
  });
  const serviceNoms = useMemo(
    () => nomListQ.data?.items.filter((n) => n.is_service && !n.deletion_mark) ?? [],
    [nomListQ.data],
  );

  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const taxPurposesList = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });

  const docQuery = useQuery({
    queryKey: ['service-act', id],
    queryFn: () => serviceActs.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);
  const defaultTaxPurposeId = useMemo(
    () => taxPurposesList.data?.find((tp) => tp.code === 'TAXABLE')?.id ?? null,
    [taxPurposesList.data],
  );

  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  const showVat = currentOrg?.is_vat_payer ?? true;
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveVatId = showVat ? defaultVatId : exemptVatId;

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1, effectiveVatId, defaultTaxPurposeId)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id ?? '',
        project_id: d.project_id ?? null,
        service_period_from: d.service_period_from ?? '',
        service_period_to: d.service_period_to ?? '',
        prices_include_vat: d.prices_include_vat,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          description: it.description,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          tax_purpose_id: it.tax_purpose_id ?? null,
          accounting_account_code: it.accounting_account_code ?? '703',
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs, effectiveVatId, defaultTaxPurposeId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  function setItem(i: number, patch: Partial<ServiceActItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // Recalc.
        const vat = vatList.data?.find((v) => v.id === next.vat_rate_id);
        const rate = vat?.rate_percent ?? 0;
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch) {
          const qty = Number(next.quantity || 0);
          const price = Number(next.price || 0);
          const awv = qty * price;
          const vatAmt = (awv * Number(rate)) / 100;
          next.amount_without_vat = awv.toFixed(2);
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, newRow(f.items.length + 1, effectiveVatId, defaultTaxPurposeId)],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const create = useMutation({
    mutationFn: () => serviceActs.create({
      organization_id: form.organization_id,
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      project_id: form.project_id,
      service_period_from: form.service_period_from || null,
      service_period_to: form.service_period_to || null,
      prices_include_vat: form.prices_include_vat,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['service-acts'] });
      navigate(`/service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => serviceActs.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      project_id: form.project_id,
      service_period_from: form.service_period_from || null,
      service_period_to: form.service_period_to || null,
      prices_include_vat: form.prices_include_vat,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-act', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await serviceActs.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        project_id: form.project_id,
        service_period_from: form.service_period_from || null,
        service_period_to: form.service_period_to || null,
        prices_include_vat: form.prices_include_vat,
        comment: form.comment || null,
        items: form.items,
      });
      await serviceActs.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['service-acts'] });
      navigate(`/service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await serviceActs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        project_id: form.project_id,
        service_period_from: form.service_period_from || null,
        service_period_to: form.service_period_to || null,
        prices_include_vat: form.prices_include_vat,
        comment: form.comment || null,
        items: form.items,
      });
      await serviceActs.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-act', id] });
      qc.invalidateQueries({ queryKey: ['service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => serviceActs.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-act', id] });
      qc.invalidateQueries({ queryKey: ['service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => serviceActs.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-acts'] });
      navigate('/service-acts');
    },
  });

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий акт послуг' : `Акт послуг: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/service-acts')}>Скасувати</button>
          {!isPosted && (
            <button className="primary"
                    onClick={() => isNew ? create.mutate() : update.mutate()}
                    disabled={create.isPending || update.isPending || createAndPost.isPending}>
              💾 Зберегти
            </button>
          )}
          {isNew && (
            <button className="primary"
                    onClick={() => createAndPost.mutate()}
                    disabled={create.isPending || createAndPost.isPending}
                    title="Зберігає та проводить документ">
              ▶ Провести
            </button>
          )}
          {!isNew && !isPosted && form.items.length > 0 && (
            <button className="primary" onClick={() => updateAndPost.mutate()}
                    disabled={updateAndPost.isPending || update.isPending}
                    title="Зберігає поточні зміни та проводить документ">
              ▶ Провести
            </button>
          )}
          {!isNew && isPosted && (
            <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
              ◀ Розпровести
            </button>
          )}
          {!isNew && !isPosted && (
            <button className="danger"
                    onClick={() => confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); })}>
              Видалити
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew || isPosted}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>№ документа{' '}
            {isNew && <small style={{ color: 'var(--muted)' }}>(auto, якщо порожнє)</small>}
          </label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 disabled={isPosted}
                 placeholder={isNew ? 'АП-0001-2026 (auto)' : undefined} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Замовник</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {buyers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Договір{' '}
            <small style={{ color: 'var(--muted)' }}>
              (необов'язково — буде створено автоматично)
            </small>
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">— без договору —</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Період надання послуг з</label>
          <DatePopover value={form.service_period_from}
                 onChange={(v) => setForm({ ...form, service_period_from: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>По</label>
          <DatePopover value={form.service_period_to}
                 onChange={(v) => setForm({ ...form, service_period_to: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Послуги</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Послуга</th>
            <th style={{ padding: '0.5rem' }}>Опис</th>
            <th style={{ padding: '0.5rem', width: 90 }}>К-ть</th>
            <th style={{ padding: '0.5rem', width: 110 }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Сума</th>
            {showVat && (
              <>
                <th style={{ padding: '0.5rem', width: 100 }}>ПДВ %</th>
                <th style={{ padding: '0.5rem', width: 110 }}>ПДВ</th>
                <th style={{ padding: '0.5rem', width: 130 }}>Всього</th>
              </>
            )}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.nomenclature_id}
                        onChange={(e) => setItem(i, { nomenclature_id: e.target.value })}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {serviceNoms.map((n) => (
                    <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                  ))}
                </select>
                {serviceNoms.length === 0 && (
                  <small style={{ color: '#dc2626' }}>
                    Немає послуг у довіднику. Створіть номенклатуру з ✓ is_service.
                  </small>
                )}
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input value={it.description ?? ''}
                       onChange={(e) => setItem(i, { description: e.target.value })}
                       disabled={isPosted}
                       placeholder="Опис послуги (необов'язково)" />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItem(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.price}
                       onChange={(e) => setItem(i, { price: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                {fmt(Number(it.amount_without_vat))}
              </td>
              {showVat && (
                <>
                  <td style={{ padding: '0.5rem' }}>
                    <select value={it.vat_rate_id}
                            onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                            disabled={isPosted}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {fmt(Number(it.vat_amount))}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                    {fmt(Number(it.total_amount))}
                  </td>
                </>
              )}
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={5} style={{ padding: '0.5rem', textAlign: 'right' }}>Разом:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {showVat && (
              <>
                <td></td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}

      <div className="field" style={{ marginTop: '0.75rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}


function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: { data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> } }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
